/**
 *  @class
 *  @function CollectionTabs
 */

if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    constructor() {
      super();
      this._onResize = null;
      this._onBlockSelect = null;
      this._fetchCache = new Map();
      this._fetchPromises = new Map();
      this._heightTimeout = null;
    }

    connectedCallback() {
      // Attach the block-select listener immediately so we don't miss the
      // event Shopify fires right after a section reload, before our rAF tick.
      if (Shopify.designMode) {
        this._onBlockSelect = (event) => {
          const buttons = this.buttons || Array.from(this.querySelectorAll('button'));
          const index = buttons.indexOf(event.target);

          if (index === -1) return;

          requestAnimationFrame(() => {
            buttons[index].click();
          });
        };

        this.addEventListener('shopify:block:select', this._onBlockSelect);
      }

      requestAnimationFrame(() => {
        this.buttons = Array.from(this.querySelectorAll('button'));

        let sectionHeader = this.closest('.section-header');

        this.links = sectionHeader
          ? Array.from(sectionHeader.querySelectorAll('.linked-to-tab'))
          : [];

        this.target = this.dataset.target;
        this.slider = document.getElementById(this.target);

        if (!this.slider) return;

        this.productLimit = parseInt(this.slider.dataset.productLimit, 10) || 6;

        // Cache items grouped by collection handle
        this.collectionMap = new Map();
        this.allItems = this.slider.querySelectorAll('[data-collection]');

        this.allItems.forEach((item) => {
          let handle = item.dataset.collection;

          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }

          this.collectionMap.get(handle).push(item);
        });

        // Event delegation instead of per-button listeners
        this.addEventListener('click', (event) => {
          let button = event.target.closest('button');

          if (!button) return;

          event.preventDefault();

          let index = this.buttons.indexOf(button);

          if (index === -1) return;

          this.toggleButtons(index);
          this.toggleLinks(index);

          if (button.dataset.collection) {
            this.handleTabClick(button);
          }
        });

        // Resize handler
        this._onResize = debounce(() => {
          this.refreshSliderHeight();
        }, 150);

        window.addEventListener('resize', this._onResize);

        // Herbereken de carousel zodra de pagina volledig geladen is.
        if (document.readyState === 'complete') {
          this.refreshSliderHeight();
        } else {
          window.addEventListener(
            'load',
            () => {
              this.refreshSliderHeight();
            },
            { once: true }
          );
        }

        // Afbeeldingen kunnen de hoogte van productkaarten later nog wijzigen.
        this.watchProductImages();

        // WebwinkelKeur kan inhoud dynamisch aanpassen.
        this.watchProductCardChanges();

        // Lazy-load inactive tabs after page load
        if (!Shopify.designMode) {
          this.prefetchInactiveTabs();
        }
      });
    }

    disconnectedCallback() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }

      if (this._onBlockSelect) {
        this.removeEventListener('shopify:block:select', this._onBlockSelect);
      }

      if (this._heightTimeout) {
        clearTimeout(this._heightTimeout);
      }

      if (this._productObserver) {
        this._productObserver.disconnect();
      }
    }

    getFlickity() {
      if (!this.slider) return null;

      return Flickity.data(this.slider) || null;
    }

    /**
     * Zorgt dat Flickity opnieuw rekening houdt met de actuele
     * hoogte van de productkaarten.
     */
    refreshSliderHeight() {
      if (!this.slider) return;

      let flkty = this.getFlickity();

      if (!flkty) return;

      // Eerste herberekening
      flkty.reloadCells();
      flkty.resize();

      // Flickity opnieuw positioneren op de huidige slide.
      if (typeof flkty.reposition === 'function') {
        flkty.reposition();
      }

      // Images, fonts en externe widgets kunnen iets later renderen.
      // Daarom nog een extra meting na de browser-render.
      requestAnimationFrame(() => {
        let currentFlickity = this.getFlickity();

        if (!currentFlickity) return;

        currentFlickity.reloadCells();
        currentFlickity.resize();

        if (typeof currentFlickity.reposition === 'function') {
          currentFlickity.reposition();
        }
      });

      // Laatste controle nadat dynamische inhoud tijd heeft gehad om
      // volledig te renderen.
      if (this._heightTimeout) {
        clearTimeout(this._heightTimeout);
      }

      this._heightTimeout = setTimeout(() => {
        let currentFlickity = this.getFlickity();

        if (!currentFlickity) return;

        currentFlickity.reloadCells();
        currentFlickity.resize();

        if (typeof currentFlickity.reposition === 'function') {
          currentFlickity.reposition();
        }
      }, 300);
    }

    /**
     * Productafbeeldingen kunnen na initialisatie van Flickity laden
     * en daardoor de hoogte van een kaart veranderen.
     */
    watchProductImages() {
      if (!this.slider) return;

      let images = this.slider.querySelectorAll('img');

      images.forEach((image) => {
        if (image.complete) return;

        image.addEventListener(
          'load',
          () => {
            this.refreshSliderHeight();
          },
          { once: true }
        );
      });
    }

    /**
     * Houd wijzigingen in productkaarten in de gaten.
     * Dit vangt onder andere dynamisch ingeladen reviewcontent op.
     */
    watchProductCardChanges() {
      if (!this.slider || typeof MutationObserver === 'undefined') return;

      this._productObserver = new MutationObserver((mutations) => {
        let shouldRefresh = mutations.some((mutation) => {
          if (mutation.type !== 'childList') return false;

          return (
            mutation.addedNodes.length > 0 ||
            mutation.removedNodes.length > 0
          );
        });

        if (shouldRefresh) {
          this.refreshSliderHeight();
        }
      });

      this._productObserver.observe(this.slider, {
        childList: true,
        subtree: true
      });
    }

    toggleButtons(index) {
      this.buttons.forEach((button) => button.classList.remove('active'));

      this.buttons[index].classList.add('active');
    }

    toggleLinks(index) {
      if (this.links.length && this.links[index]) {
        this.links.forEach((link) => link.classList.remove('active'));

        this.links[index].classList.add('active');
      }
    }

    toggleCollection(handle) {
      if (!this.slider) return;

      let flkty = this.getFlickity();
      let activeItems = this.collectionMap.get(handle) || [];

      // Nothing to show (e.g. the fetch failed) — keep the current tab
      if (!activeItems.length) return;

      // Remove carousel__slide from all
      this.allItems.forEach((item) => {
        item.classList.remove('carousel__slide');
      });

      // Add carousel__slide to active items and ensure they're
      // in the flickity-slider container
      let flickitySlider = flkty
        ? this.slider.querySelector('.flickity-slider')
        : null;

      activeItems.forEach((item) => {
        item.classList.add('carousel__slide');

        if (flickitySlider && item.parentNode !== flickitySlider) {
          flickitySlider.appendChild(item);
        }
      });

      if (flkty) {
        flkty.reloadCells();
        flkty.select(0, false, true);
        flkty.resize();

        if (typeof flkty.reposition === 'function') {
          flkty.reposition();
        }

        // Nogmaals berekenen nadat de nieuwe productkaarten
        // daadwerkelijk door de browser zijn gerenderd.
        requestAnimationFrame(() => {
          this.refreshSliderHeight();
        });
      }

      // Nieuwe afbeeldingen observeren
      this.watchProductImages();
    }

    handleTabClick(button) {
      let handle = button.dataset.collection;

      if (this.collectionMap.has(handle)) {
        this.toggleCollection(handle);
        return;
      }

      // Products not loaded yet — fetch on demand
      let url = button.dataset.collectionUrl;

      if (!url) {
        this.toggleCollection(handle);
        return;
      }

      this.fetchCollection(handle, url).then(() => {
        this.toggleCollection(handle);
      });
    }

    prefetchInactiveTabs() {
      let loadTabs = () => {
        this.buttons.forEach((button, index) => {
          if (index === 0) return;

          let handle = button.dataset.collection;
          let url = button.dataset.collectionUrl;

          if (!handle || !url || this.collectionMap.has(handle)) return;

          this.fetchCollection(handle, url);
        });
      };

      if (document.readyState === 'complete') {
        loadTabs();
      } else {
        window.addEventListener('load', loadTabs, { once: true });
      }
    }

    fetchCollection(handle, collectionUrl) {
      if (this._fetchCache.has(handle)) {
        return Promise.resolve();
      }

      if (this._fetchPromises.has(handle)) {
        return this._fetchPromises.get(handle);
      }

      let fetchUrl = collectionUrl + '?section_id=collection-tabs-ajax';

      let promise = fetch(fetchUrl)
        .then((response) => response.text())
        .then((html) => {
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');

          let items = Array.from(
            doc.querySelectorAll('[data-collection]')
          ).slice(0, this.productLimit);

          // Check for a pre-rendered promotion template for this collection
          let sectionId = this.slider.dataset.id;

          let promotionTemplate = document.querySelector(
            `template[data-promotion-section="${sectionId}"][data-promotion-collection="${handle}"]`
          );

          let promotionItems = [];

          if (promotionTemplate) {
            let promoClone = promotionTemplate.content.cloneNode(true);

            promotionItems = Array.from(
              promoClone.querySelectorAll('[data-collection]')
            );

            promotionTemplate.remove();
          }

          let allNewItems = [...promotionItems, ...items];

          // Insert into the Flickity slider container
          // (or slider root if Flickity not initialized)
          let flickitySlider =
            this.slider.querySelector('.flickity-slider');

          let container = flickitySlider || this.slider;

          allNewItems.forEach((item) => {
            container.appendChild(item);
          });

          // Initialize lazy-loaded images
          container.querySelectorAll('.lazyload').forEach((img) => {
            lazySizes.loader.unveil(img);
          });

          // Update collectionMap and allItems
          if (!this.collectionMap.has(handle)) {
            this.collectionMap.set(handle, []);
          }

          allNewItems.forEach((item) => {
            this.collectionMap.get(handle).push(item);
          });

          this.allItems =
            this.slider.querySelectorAll('[data-collection]');

          this._fetchCache.set(handle, true);
          this._fetchPromises.delete(handle);

          // Nieuwe content is toegevoegd: hoogte opnieuw berekenen.
          this.watchProductImages();
          this.refreshSliderHeight();
        })
        .catch((e) => {
          console.warn(
            'Collection tabs: failed to load collection "' + handle + '"',
            e
          );

          this._fetchPromises.delete(handle);
        });

      this._fetchPromises.set(handle, promise);

      return promise;
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
}
